//React & Redux
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

//Internal Compoments
import TestFooter from "../../components/layouts/Footer/TestFooter";
import Header from "../../components/layouts/Header/Header";
import PageContainer from "../../components/layouts/Containers/PageContainer";

// External Components
// Sanity Client
import { client } from "../../services/client";

//CSS
import "./about.css";
import SEO from "../../components/seo/SEO";

function CommonsExplainerScreen() {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [pageContent, setPageContent] = useState();

  async function getPosts() {
    const posts = await client.fetch(`*[_type == "photo" ]{
  _id,
  title,
  "imageUrl": imageFile.asset->url,
  altText,
  caption,
  description
}`);
    return posts;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getPosts();

        if (response) {
          setPageContent(response);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <SEO
        title="Digital Commons Platform"
        description="A commons is a responsibly managed shared resource upon which people can rely for some part of their livelihood. With the support of a steward who oversees the commons, and is ultimately responsible to the needs of the 'commoners' (the people who use and benefit from the commons), the resource is maintained and protected for present and future benefit. I believe we can create and scale digital ecosystems with this same fundamental ethos. From here on in, I will refer to such entities as a Digital Commons Platform or DCP."
        canonicalUrl="https://nowasteddays.io/nowasteddays-commons"
      />
      {userInfo ? <Header variant="light" /> : <Header variant="light" />}
      <PageContainer className="aboutpage">
        <div className="tile missionScreenTile">
          <div className="missionScreenContent">
            <div className="missionScreenText">
              <div>
                <strong>A Digital Commons Platform (DCP).</strong>
              </div>
              <p>
                A commons is a responsibly managed shared resource upon which
                people can rely for some part of their livelihood. With the
                support of a steward who oversees the commons, and is ultimately
                responsible to the needs of the 'commoners' (the people who use
                and benefit from the commons), the resource is maintained and
                protected for present and future benefit. I believe we can
                create and scale digital ecosystems with this same fundamental
                ethos. From here on in, I will refer to such entities as a
                Digital Commons Platform or DCP.
              </p>

              <div>
                <strong>The Three Swimming Pools.</strong>
              </div>
              <p>
                To tell the story of why this might be a valid and valuable
                idea, imagine you were choosing between three swimming pools.
              </p>
              <p>
                <strong>Swimming Pool #1:</strong> The first pool is one where
                locals can book a slot and enjoy a swim for whatever their
                health or leisure needs might be at any particular time. They
                can either use the pool for free or, if they have the means,
                donate to its upkeep and development. Whether as a patron or a
                free user, the terms of use are designed to enshrine the
                benefits of the pool to you and the community ahead of any other
                concern. The financial resources exist in perpetuity to keep it
                at its current state and scale; serving locals as per their
                needs consistently over a long period of time.
              </p>
              <div className="mediaPlaceholder">
                {pageContent &&
                  pageContent
                    .filter((f) => f.title === "SWIMMING_POOL_ONE")
                    .map((img, index) => (
                      <div
                        key={index}
                        style={{
                          width: "100%",
                          borderRadius: "5px",
                          textAlign: "start",
                        }}
                      >
                        <img
                          style={{ width: "100%", borderRadius: "5px" }}
                          src={img.imageUrl}
                          alt={img.altText}
                        />
                        <label>figure a: {img.caption}</label>
                      </div>
                    ))}
              </div>
              <p>
                In order to expand the pool facilities and benefits to more
                people (if the demand dictates the validity of doing so), the
                pool team is constantly monitoring donations and reviewing
                feedback about the pool to sustainably open the doors to new
                swimmers i.e. within the scope of what is going to keep the pool
                available, safe and beneficial for all. Certain measures are put
                in place to achieve this such as a waiting list from one month
                to the next, closely managing the number of available slots on
                any given day and keeping everyone informed about the state of
                the pool. These small interventions are efficient and effective
                ways of preserving the practical purpose of the pool as well as
                extending its benefits into the future.
              </p>
              <hr></hr>
              <p>
                <strong>Swimming Pool #2:</strong> The owners of this pool don't
                want to be trapped by waiting lists or growth constraints so
                they create a humongous swimming pool and provide it for free!
                In a very short time, your friends are all using this pool, and
                it all looks pretty flash and cool. However, in order to
                facilitate this initial scale and to keep up some hefty growth
                targets, the terms of use are very different to Swimming Pool
                #1. In the terms and conditions, there is a clause that states
                that third parties can open up your lockers and study your
                belongings to learn about you whilst you are having a swim, and
                that advertisers can roam the pool side looking to sell you
                things you may or may not want. Over time, the swimming pool #2
                managers can't ignore that advertisers are the key stakeholders
                driving the goals of the facility so the pool environment gets
                optimised with them in mind with billboards, flashing signs and
                even greater access to swimmers. In addition, this also inspires
                no small amount of cost cutting. The water is green and dirty.
                The receptionist has been fired meaning any number of unverified
                people can enter the pool. Swimmers are invited to share
                feedback to improve the service, but the suggestion box gathers
                dust; willfully ignored by the remaining staff who are simply
                trying to ensure the advertisers are keen to return.
              </p>
              <div className="mediaPlaceholder">
                {pageContent &&
                  pageContent
                    .filter((f) => f.title === "SWIMMING_POOL_TWO")
                    .map((img, index) => (
                      <div
                        key={index}
                        style={{ width: "100%", borderRadius: "5px" }}
                      >
                        <img
                          style={{ width: "100%", borderRadius: "5px" }}
                          src={img.imageUrl}
                          alt={img.altText}
                        />
                        <label>figure a: {img.caption}</label>
                      </div>
                    ))}
              </div>
              <p>
                Your friends don't seem to mind any of this, or don't seem to
                have noticed, and some have even paid for 'Swimming Pool Two+'
                which means they get access to a parallel pool without the
                advertisers. The pool persists despite its concerning and
                damaging features.
              </p>
              <hr></hr>
              <p>
                <strong>Swimming Pool #3:</strong> Concerned by the chaos of
                Swimming Pool #2, another pool is set up wherein everyone pays a
                significant fixed monthly subscription price for access to a
                premium pool. For those who can afford it, this eliminates many
                of the concerns of Pool #2. It's nice and clean, no more
                advertisers and has good safety protocols. However, it does mean
                that people without the means to pay are permanently excluded
                from not just the pool but the mental and physical benefits of
                access; two things that would create positive ripple effects
                across the rest of their lives.
              </p>
              <div className="mediaPlaceholder">
                {pageContent &&
                  pageContent
                    .filter((f) => f.title === "SWIMMING_POOL_THREE")
                    .map((img, index) => (
                      <div
                        key={index}
                        style={{ width: "100%", borderRadius: "5px" }}
                      >
                        <img
                          style={{ width: "100%", borderRadius: "5px" }}
                          src={img.imageUrl}
                          alt={img.altText}
                        />
                        <label>figure c: {img.caption}</label>
                      </div>
                    ))}
              </div>
              <hr />
              <p style={{ fontStyle: "italic", fontWeight: "bold" }}>
                Which swimming pool setup do you think would work best for you?
                Which do you think works best for the groups described, and not
                just any one individual? Assuming we are always going to exist
                in a world with these 3 pools exist, what percentage of all
                pools should be like pools #1, #2 and #3?
              </p>
              <hr />
              <p>
                I'll let you decide for yourself. However, I will not be shy in
                saying that I am really interested in Swimming Pool #1. This
                pool describes the DCP model whereas pool #2 is a 'freemium'
                start-up model, and #3 is a classic premium digital subscription
                model. Within the DCP model lays the chance to keep as much of
                the digitally unlocked human upside as you'd hope from any well
                built online platform all whilst protecting against any such
                damaging individual and societal downside. Scaling takes a
                different form, but these measures can pay dividends in ensuring
                growth is sustainable and that you deepen social impact at the
                same time.
              </p>
              <p></p>
              <div>
                <strong>How to run a Digital Commons Platform.</strong>
              </div>
              <p>
                Beneath all of the metaphor, there are 4 features that underpin
                a Digital Commons Platform that anyone can lift and share for
                the needs of similar endeavours.
              </p>
              <ol>
                <li>
                  <strong>Protect Against Unsustainable Scaling</strong>
                </li>
                <li>
                  <strong>Gather Feedback on Impact & Improvements</strong>
                </li>
                <li>
                  <strong>Guarantee Maximum Donation Endurance</strong>
                </li>
                <li>
                  <strong>Empower Understanding of Platform Performance</strong>
                </li>
                {/* <li>
                  <strong>Provide Guidance on Responsible Usage</strong>
                </li> */}
              </ol>
              {/* Unsustainable Scaling */}
              <div>
                <strong>Protect Against Unsustainable Scaling</strong>
              </div>
              <p>
                Any digital platform has to deal with issues of scale and when
                these issues are mismanaged, there can be huge human fallout.
                Within the typical start-up model, there are many instances
                where tonnes of funding is sought to attract droves of potential
                users to a platform. When the people are there, employees are
                brought in hand over fist to support the new and novel size of
                the infrastructure. But, if this is not done smartly, the users
                will soon be off, like moths to the next flickering light in the
                digital distance, leaving lots of people out of work. In additon
                to this, the push for external funding is not only a start-ups
                worst financial decision, it can also spell disaster for
                positive impact on users. More often than not, an investor is
                preoccupied with returns and profit, rather than practical
                positive benefit to users. This puts the platform now in a
                peculiar tension between growth and preserving the initial aims
                of the platform itself. Growth being so immediately commercial
                and measurable in isolation means it tends to overwhelm the
                nuances of human centric growth. A DCP aims to avoid all of
                these adverse variances by taking a different approach to
                scaling.
              </p>
              <p>
                At whatever cadence the stewards decide, based on performance
                data and donations, the next wave of users can be introduced to
                the platform (or commoners can enter the common if you prefer
                that terminology). This might be an incremental addition, or a
                seismic change, but facilitating this intentionally is designed
                to never displace human labour, never leave people without the
                platform, never compromise platform performance and never turn
                the platform into a vehicle for pounds and pence ahead of
                positive practical impact on people. As a commons, the priority
                is positive impact on people and preserving said impact now and
                into the future. This is the enduring consideration and whilst
                scaling is as much of a concern as any private business, it is
                not the pernicious perogative that it can so often become.
              </p>
              <div>
                <strong>Gather Feedback on Impact & Improvements</strong>
              </div>
              <p>
                A commons is so named because it is there to serve commoners -
                not shareholders, landowners, directors, debtors or creditors. A
                commons can take infinite useful forms and hopefully I have
                thought of one of them in the design and purpose of the
                NoWastedDays.io platform. Once established in whatever form, a
                DCP needs to be both acutely aware of its actual demonstrable
                impact on people as well as very open to how this impact can
                increase. I will be sharing a seasonal survey to all commoners
                to help understand impact and improve the commons overall. This
                will help discern whether we can create the most impact from
                scaling outwards ('increasing the size of our digital
                infrastructure to onboard new users with no loss of
                performance') or scale inwards ('optimising existing or adding
                new features to unlock additional impact for existing users').
              </p>
              <div>
                <strong>Empower Understanding of Platform Performance</strong>
              </div>
              <p>
                Thinking and designing with the commons ethos in mind, the
                platform itself can provide visibility on its performance status
                so as a commoner / user you can easily understand why the app
                might be a little slower or a certain feature might be
                unavailable on any particular day. These elements are designed
                to nurture engaged, inquisitive and invested commoners, not
                unconscious, unempowered users. Here is a list of a few things
                you will see around the platform:
                <br></br>
                <br></br>
                <ul>
                  <li>
                    <strong>Concurrent User Status:</strong> In the footer, you
                    can see the number of concurrent users and a red, amber,
                    green icon indicating how this may be impacting performance.
                    If we ever get into the red, it will mean that we need to
                    boost current infrastructure in order to facilitate more
                    users. I have a donations page and will be finding grants
                    that can support with theses improvements.
                  </li>
                  <li>
                    <strong>Waiting List:</strong> Despite having an account
                    yourself, you may see when you login that we are in a phase
                    of preventing additional sign ups until we can guarantee
                    that the platform can onboard them with no loss in
                    performance quality.
                  </li>
                  <li>
                    <strong>AI Budget Flag:</strong> Until we move to an
                    entirely reliably open source model, each AI request -
                    whether your insight story support, journal transcription or
                    weekly planning lists - comes at a small financial cost. The
                    budget for these is finite but renews each month. As such,
                    you may see a note on the insights page, for example, that
                    says that the AI access has been turned off until the start
                    of the next month. I hope this will only be true in a very
                    small number of cases, but if you do see it, you will know
                    why.
                  </li>
                  <li>
                    <strong>Evolution of the Commons:</strong> On this page, you
                    will be able to see the month by month evolution of the
                    commons. Sometimes it will grow, sometimes it might shrink,
                    but, again, you will know why - to preserve the quality of
                    the platform performance for those getting something
                    positive from it.
                  </li>
                </ul>
              </p>
              <div>
                <strong>Guarantee Maximum Donation Endurance</strong>
              </div>
              <p>
                Any organisation looking to operate sustainably and put users
                first has to think clearly about the nature of its incomings and
                outgoings. Further to this, anyone who donates wants to see
                their donations have maximum impact on the purpose of the
                platform.
              </p>
              <p>
                To ensure a DCP does both of these things will require any
                steward to be clear on the following:
              </p>
              <ul style={{ listStyle: "none" }}>
                <li>
                  <strong>The nature and rate of its incomings:</strong>
                  <ul>
                    <li>What percentage of revenue is recurring?</li>
                    <li>What grant funding is in the pipeline?</li>
                    <li>
                      Is there a typical / reliable pattern in ad-hoc donations?
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>The nature and rate of its outgoings:</strong>
                  <ul>
                    <li>
                      Infrastructure: The technical heart of the DCP - the
                      servers, hosting, code repositories, databases.
                    </li>
                    <li>
                      Talent: Who are the people that can drive it successfully
                      at its present scale, and who may be needed based on
                      sustainable growth predictions? How much do they need to
                      be paid to rewarded appropriately for their skills and
                      expertise?
                    </li>
                    <li>
                      Marketing: What budget is needed to raise awareness of the
                      problem that the platform helps solve and help introduce
                      people who may benefit from having that problem solved to
                      the platform?
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>
                    {" "}
                    ...and how do these contrast with the next flex point in the
                    growth of the platform itself.
                  </strong>
                  <ul>
                    <li>
                      Is there a lot of headroom due to a recent uptick in
                      infrastructure, or are we close to our current ceiling?
                      Each wave of infrastructure changes makes the commons
                      bigger. In certain periods of time, we will be closer to
                      the ceiling than others.
                    </li>
                    <li>How large is the waiting list?</li>
                    <li>
                      What have users been feeding back in regards to improving
                      the platform?
                    </li>
                  </ul>
                </li>
              </ul>
              <p>
                By taking the time to consider the consequences of growth and
                the nature of donations, a DCP can be reliable, enduring and
                enriching for internal talent, commoners and donators alike, and
                be distinct from the modes of scaling technology that we have
                become all too familiar with and the costs that they carry.
              </p>
              {/* <div>
                <strong>Provide Guidance on Responsible Usage</strong>
              </div>
              <p>
                Responsible usage and management is at the heart of any thriving
                commons. Whilst subject to some famous issues, the basic
                principle governing responsible usage of a commons is a simple
                one; gather what you need so you can benefit, but not so much
                that you stop others benefitting or stop the commons renewing
                itself overall. A DCP cannot be diminished in the same way a
                forest can be over cut, or a common land over grazed, but usage
                will incur costs; direct financial costs and performance costs.
                A DCP can provide clear guidance on how any 'commoner' can use
                the resource in line with the principle above.
              </p>
              <p>
                At present, the responsible usage guidance on NoWastedDays.io is
                as follows:
              </p>
              <ul>
                <li>The average number of sessions per day per user is X</li>
                <li>
                  The average number of requests made per day per user is X
                </li>
                <li>
                  Versus our current infrastructure, I can support 1000 users,
                  starting 10 sessions per day, making 100 requests per day,
                  without any significant performance loss.
                </li>
                <li>
                  I will be updating these numbers and will refer to them in the
                  state of the commons monthly emails.
                </li>
              </ul>
              <div className="mediaPlaceholder">
                {pageContent &&
                  pageContent
                    .filter((f) => f.title === "RESPONSIBLE_USAGE_GUIDANCE")
                    .map((img, index) => (
                      <div
                        key={index}
                        style={{ width: "100%", borderRadius: "5px" }}
                      >
                        <img
                          style={{ width: "100%", borderRadius: "5px" }}
                          src={img.imageUrl}
                          alt={img.altText}
                        />
                        <label>figure d: {img.altText}</label>
                      </div>
                    ))}
              </div>
              <p>
                As you can see above, versus our current infrastructure,
                financial resources and user levels, with the exception of the
                AI requests that carry a heightened financial cost, no one
                action on the platform is that costly in terms of performance
                i.e. going to make the average experience slower or less
                effectual than you'd hope. Whether it is updating your vision
                for the year, adding to your plans for the week, or saving your
                daily journal log, all of that should carry very low performance
                impact so do it as much as you need.
              </p> */}
            </div>

            <div className="missionScreenMedia">
              <div>
                <strong>The State of our Commons.</strong>
              </div>
              <p>First Update Coming Soon.</p>
            </div>
          </div>
        </div>
      </PageContainer>
      <TestFooter />
    </>
  );
}

export default CommonsExplainerScreen;
